/**
 * ============================================================
 * KEJJA OS — src/hooks/useNotifications.ts (extended with sync)
 * ============================================================
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type Notification, type ListFilter, type UpsertPrefPayload,
} from '@/lib/notificationsApi';
import { useNotificationSound } from './useNotificationSound';

const POLL_INTERVAL_MS = 60_000;

export interface SyncEvent {
  module: string;
  homeId: string;
  action: 'created' | 'updated' | 'deleted' | 'changed';
  entityType?: string;
  entityId?: string;
  extra?: Record<string, unknown>;
}

// ============================================================
// Queries / mutations
// ============================================================

export function useNotifications(filter: ListFilter = 'all') {
  return useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => notificationsApi.list(filter),
    refetchInterval: POLL_INTERVAL_MS,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 10_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useDismissAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.dismissAll(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
}

export function useTestNotification() {
  return useMutation({ mutationFn: () => notificationsApi.sendTest() });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  });
}

export function useUpsertPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertPrefPayload) => notificationsApi.upsertPreference(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] }); },
  });
}

// ============================================================
// Sync event → query invalidation mapping
// ============================================================

function invalidateForSync(qc: QueryClient, event: SyncEvent): void {
  const { module, homeId } = event;

  // Generic: invalidate module-scoped queries
  qc.invalidateQueries({ queryKey: [module, homeId] });

  // Per-module specifics
  switch (module) {
    case 'pantry':
      qc.invalidateQueries({ queryKey: ['pantry', homeId] });
      qc.invalidateQueries({ queryKey: ['pantry', homeId, 'pulse'] });
      break;
    case 'community':
      qc.invalidateQueries({ queryKey: ['community', homeId] });
      // If we know which community, scope it
      if (event.extra?.communityId) {
        qc.invalidateQueries({
          queryKey: ['community', homeId, 'one', event.extra.communityId],
        });
        qc.invalidateQueries({
          queryKey: ['community', homeId, 'posts', event.extra.communityId],
        });
        qc.invalidateQueries({
          queryKey: ['community', homeId, 'goals', event.extra.communityId],
        });
        qc.invalidateQueries({
          queryKey: ['community', homeId, 'contributions', event.extra.communityId],
        });
      }
      break;
    case 'calendar':
      qc.invalidateQueries({ queryKey: ['calendar', homeId] });
      break;
    case 'meals':
    case 'shopping':
      qc.invalidateQueries({ queryKey: ['meals', homeId] });
      qc.invalidateQueries({ queryKey: ['shopping', homeId] });
      break;
    case 'library':
      qc.invalidateQueries({ queryKey: ['library', homeId] });
      break;
    case 'finance':
      qc.invalidateQueries({ queryKey: ['finance', homeId] });
      break;
    case 'routines':
      qc.invalidateQueries({ queryKey: ['routines', homeId] });
      break;
  }
}

// ============================================================
// Combined SSE pump — handles notifications + sync events
// ============================================================

export function useNotificationPump(enabled = true) {
  const qc = useQueryClient();
  const { playChime } = useNotificationSound();
  const esRef = useRef<EventSource | null>(null);

  const handleNotification = useCallback((n: Notification) => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    const variant = n.priority === 'critical' ? 'critical'
      : n.priority === 'quiet' ? 'quiet' : 'normal';
    playChime(variant);
  }, [qc, playChime]);

  const handleSync = useCallback((event: SyncEvent) => {
    invalidateForSync(qc, event);
  }, [qc]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof EventSource === 'undefined') return;

    const es = new EventSource(notificationsApi.streamUrl(), { withCredentials: true });
    esRef.current = es;

    const notifHandler = (e: MessageEvent) => {
      try { handleNotification(JSON.parse(e.data) as Notification); }
      catch (err) { console.warn('[sse] notif parse error', err); }
    };

    const syncHandler = (e: MessageEvent) => {
      try { handleSync(JSON.parse(e.data) as SyncEvent); }
      catch (err) { console.warn('[sse] sync parse error', err); }
    };

    es.addEventListener('notification', notifHandler as EventListener);
    es.addEventListener('sync', syncHandler as EventListener);

    return () => {
      es.removeEventListener('notification', notifHandler as EventListener);
      es.removeEventListener('sync', syncHandler as EventListener);
      es.close();
      esRef.current = null;
    };
  }, [enabled, handleNotification, handleSync]);
}
